import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { GlassCard, EmptyState } from "@/components/ui-kit";
import { Target, Crown, Medal, Pencil, TrendingUp, Sparkles } from "lucide-react";

const r1 = (x) => Math.round(x * 10) / 10;
const colors = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32" };
const heights = { 1: "h-36", 2: "h-28", 3: "h-20" };
const TABS = [
  { key: "all", label: "كل المدارس" },
  { key: "small", label: "الصغرى (<150)" },
  { key: "medium", label: "المتوسطة (151-400)" },
  { key: "large", label: "الكبرى (>400)" },
];

export default function CrowningPlatform({ highlightSchoolId = null, adminEdit = false }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats/target-ranking").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const ranking = data.ranking || [];
  const target = data.target || 90;
  const view = tab === "all" ? ranking : ranking.filter((r) => r.tier === tab);
  const rankOf = (s) => (tab === "all" ? s.rank : s.rank_in_tier);
  const top3 = view.slice(0, 3);
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  const my = highlightSchoolId ? ranking.find((x) => x.school_id === highlightSchoolId) : null;
  const tierPeers = my ? ranking.filter((r) => r.tier === my.tier) : [];
  const tierFirst = tierPeers[0];
  const gap = my && tierFirst ? Math.max(r1(tierFirst.participation_rate - my.participation_rate), 0) : 0;

  return (
    <div className="space-y-5" data-testid="crowning-platform">
      {my && (
        <motion.div
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl p-5 border border-sky-300/60 bg-gradient-to-l from-sky-50 via-white to-amber-50"
          data-testid="my-rank-card"
        >
          <div className="absolute -left-8 -top-8 w-32 h-32 rounded-full bg-amber-400/15 blur-2xl" />
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <TrendingUp className="text-sky-600" size={20} />
            <h3 className="font-head font-bold text-lg text-stone-800">مركز مدرستك في المنافسة</h3>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">{my.tier_label}</span>
          </div>
          {my.rank_in_tier === 1 && tierPeers.length > 1 ? (
            <p className="text-stone-700 text-sm sm:text-base">
              🥇 <b className="text-amber-600">{my.school_name}</b> في <b>صدارة</b> {my.tier_label} بنسبة مشاركة <b className="text-emerald-600">{my.participation_rate}%</b> — أنت قدوة شريحتك!
            </p>
          ) : (
            <p className="text-stone-700 text-sm sm:text-base">
              مدرستك <b className="text-sky-700">{my.school_name}</b> في <b className="text-amber-600">المركز {my.rank_in_tier}</b> من {tierPeers.length} ضمن {my.tier_label} — نسبتك <b className="text-sky-700">{my.participation_rate}%</b>{tierPeers.length > 1 && my.rank_in_tier > 1 ? <> ، وتفصلك <b className="text-amber-600">{gap}%</b> عن متصدّر شريحتك 🚀</> : <> — الهدف {target}% 🎯</>}
            </p>
          )}
          <div className="mt-3 h-2.5 rounded-full bg-stone-200/70 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(my.participation_rate, 100)}%`, background: my.participation_rate >= target ? "#10B981" : "linear-gradient(90deg,#0EA5E9,#D4AF37)" }} />
          </div>
          <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{my.student_volunteers} من {my.students_total} طالب — الهدف {target}%</span>
            <span className="inline-flex items-center gap-1 text-amber-700"><Sparkles size={11} /> نقاط الأثر: {my.impact_points}</span>
          </div>
        </motion.div>
      )}

      <GlassCard>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Crown className="text-amber-500" size={22} />
            <h3 className="font-head font-bold text-lg text-stone-800">منصة التتويج الكبرى — منافسة عادلة حسب حجم المدرسة</h3>
          </div>
          {adminEdit && (
            <button data-testid="edit-schools-btn" onClick={() => navigate("/schools")} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 transition">
              <Pencil size={14} /> تعديل بيانات المدارس
            </button>
          )}
        </div>

        {/* Tier tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tier-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${tab === t.key ? "bg-amber-400 text-stone-900" : "bg-amber-100/60 text-stone-600 hover:bg-amber-200/70"}`}>
              {t.label}{t.key !== "all" && data.tier_totals ? ` (${data.tier_totals[t.key] || 0})` : ""}
            </button>
          ))}
        </div>

        {!view.length ? (
          <EmptyState text="لا توجد مدارس بإجمالي طلاب مُدخل في هذه الشريحة بعد" icon={Target} />
        ) : (
          <>
            {/* Podium */}
            <div className="flex items-end justify-center gap-3 sm:gap-6 mb-6">
              {podiumOrder.map((b) => {
                const pr = rankOf(b);
                return (
                  <motion.div key={b.school_id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: pr * 0.1 }}
                    className="flex flex-col items-center w-24 sm:w-40" data-testid={`podium-${pr}`}>
                    <div className="relative mb-2">
                      {pr === 1 && <div className="absolute inset-0 rounded-full blur-xl animate-pulse" style={{ background: "#FFD70066" }} />}
                      <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2" style={{ borderColor: colors[pr], background: `${colors[pr]}22` }}>
                        {pr === 1 ? <Crown size={30} style={{ color: colors[1] }} /> : <Medal size={26} style={{ color: colors[pr] }} />}
                      </div>
                    </div>
                    <div className="text-center mb-2 w-full">
                      <div className="text-sm font-semibold text-stone-800 truncate">{b.school_name}</div>
                      <div className="text-[11px] text-stone-500 truncate">{b.coordinator_name || "—"}</div>
                      <div className="text-sm font-bold" style={{ color: b.reached ? "#10B981" : "#D4AF37" }}>{b.participation_rate}%{b.reached && " ✓"}</div>
                    </div>
                    <div className={`w-full ${heights[pr]} rounded-t-xl glass flex items-start justify-center pt-2 font-head font-black text-2xl`} style={{ color: colors[pr] }}>{pr}</div>
                  </motion.div>
                );
              })}
            </div>

            {/* Ranking list */}
            <div className="space-y-2 max-h-[26rem] overflow-y-auto pe-1">
              {view.map((s) => {
                const pr = rankOf(s);
                const mine = s.school_id === highlightSchoolId;
                const medal = pr <= 3 ? colors[pr] : "rgba(14,165,233,0.15)";
                return (
                  <div key={s.school_id} data-testid={`rank-row-${pr}`}
                    className={`flex items-center gap-3 py-2 px-3 rounded-xl transition ${mine ? "bg-sky-100/80 ring-2 ring-sky-300" : "hover:bg-amber-100/60"}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ background: medal, color: pr <= 3 ? "#1A120B" : "#0284C7" }}>{pr}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-stone-800 font-medium truncate">
                          {s.school_name}{mine && " (مدرستك)"}
                          {tab === "all" && <span className="text-[10px] text-stone-400 mr-1">· {s.tier_label}</span>}
                        </span>
                        <span className="text-sm font-bold shrink-0" style={{ color: s.reached ? "#10B981" : s.participation_rate >= 60 ? "#D4AF37" : "#F59E0B" }}>{s.participation_rate}%{s.reached && " ✓"}</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-stone-200/70 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(s.participation_rate, 100)}%`, background: s.reached ? "#10B981" : "linear-gradient(90deg,#0EA5E9,#D4AF37)" }} />
                      </div>
                      <div className="text-[10px] text-stone-400 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{s.student_volunteers} من {s.students_total} طالب — {s.coordinator_name || "—"}</span>
                        {s.tier === "large" && <span className="inline-flex items-center gap-1 text-amber-600"><Sparkles size={10} /> نقاط أثر {s.impact_points}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}
